/* eslint-disable no-nested-ternary */
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faChevronDown } from '@fortawesome/sharp-solid-svg-icons';
import { useRouter } from 'next/navigation';
import React, {
  Dispatch,
  SetStateAction,
  useCallback,
  useMemo,
  useState,
} from 'react';
import { twMerge } from 'tailwind-merge';

import { DivButton } from '@/components/divButton';
import Typography from '@/components/typography';
import { DEED_PLOTTER_URL } from '@/constants/urls';
import { ShapeEditIds } from '@/features/boundaries/config';
import CloneBoundaries from '@/features/boundaries/toast/cloneBoundaries';
import ConvertLand from '@/features/boundaries/toast/convertLand';
import { ShapeIds } from '@/features/customizeMyLand/config';
import { UploadMenu } from '@/features/toolbar/uploadMenu';

import {
  DEFAULT_TOOL,
  ToolMapper,
  ToolMenu,
  ToolMenuContent,
  ToolMenuContentItem,
  toolsCustomProperties,
} from '../constants';
import { useToolbarContext } from '../provider';
import { QuickMeasureMenu } from '../quickMeasureMenu';

interface ToolItemProps {
  item: ToolMenu;
  openedToolMenu: ToolMenu | null;
  setOpenedToolMenu: Dispatch<SetStateAction<ToolMenu | null>>;
  isToolsOpen: boolean | null;
  menuIcon: ToolMenuContent | undefined;
  setMenuIcon: Dispatch<
    SetStateAction<Record<ToolMenu, ToolMenuContent | undefined>>
  >;
  disabled?: boolean;
}

const customSubMenuMap = {
  [ToolMenu.Measure]: <QuickMeasureMenu />,
  [ToolMenu.Upload]: <UploadMenu />,
};

type ToolToastPopupMap = {
  [key in ShapeEditIds]: React.ReactNode;
} & {
  [ShapeIds.BOUNDARY_CLONE]: React.ReactNode;
};

const toolToastPopupMap: ToolToastPopupMap = {
  [ShapeEditIds.MERGE_SHAPES]: <ConvertLand />,
  [ShapeEditIds.CONVERT_SHAPE]: <ConvertLand />,
  [ShapeIds.BOUNDARY_CLONE]: <CloneBoundaries />,
};

export const ToolItem: React.FC<ToolItemProps> = ({
  item,
  menuIcon,
  setMenuIcon,
  openedToolMenu,
  setOpenedToolMenu,
  isToolsOpen,
  disabled = false,
}) => {
  const router = useRouter();
  const {
    selectedTool,
    setSelectedTool,
    drawTool,
    handleBoundaryModeClick,
    handleSwappingTool,
  } = useToolbarContext();
  const [currentSubItem, setCurrentSubItem] = useState<
    ToolMenuContentItem | ToolMenuContent | undefined
  >(ToolMapper[item].menu?.[0] ?? ToolMapper[item]);
  const shouldDisplayGrid = useMemo(
    () => (ToolMapper?.[item]?.menu?.length ?? 0) > 12,
    [item]
  );

  const customSubMenu = customSubMenuMap[currentSubItem?.parent as ToolMenu];
  const toolToastPopup =
    toolToastPopupMap[currentSubItem?.boundaryMode as ShapeEditIds];

  const customClickHandler = useMemo(() => {
    return {
      [ToolMenu.Deed]: () => {
        router.push(DEED_PLOTTER_URL);
      },
    };
  }, [router]);

  const handleOnClick = useCallback(() => {
    if (disabled) return;
    if (selectedTool !== currentSubItem && selectedTool && currentSubItem) {
      if (currentSubItem.parent && customClickHandler[currentSubItem.parent]) {
        customClickHandler[currentSubItem.parent]();
      }
      setOpenedToolMenu(null);
      handleSwappingTool(currentSubItem);
    } else {
      setSelectedTool(DEFAULT_TOOL);
    }
  }, [
    currentSubItem,
    customClickHandler,
    disabled,
    handleSwappingTool,
    selectedTool,
    setOpenedToolMenu,
    setSelectedTool,
  ]);

  const handleMenuClick = useCallback(() => {
    if (disabled) return;
    if (openedToolMenu === item) {
      setOpenedToolMenu(null);
    } else if (ToolMapper[item].menu) {
      setOpenedToolMenu(item);
    }
  }, [disabled, item, openedToolMenu, setOpenedToolMenu]);

  const onItemClick = useCallback(
    (tool: ToolMenuContentItem) => {
      if (tool?.icon === undefined || disabled) return;

      if (selectedTool === tool) {
        if (openedToolMenu === item) {
          setOpenedToolMenu(null);
        }
        setSelectedTool(DEFAULT_TOOL);
      } else {
        if (tool.boundaryMode) {
          handleBoundaryModeClick(tool);
        }
        if (tool.drawMode && tool.mode) {
          drawTool?.changeMode(tool.drawMode as string, {
            styles: {
              ...toolsCustomProperties[tool.mode],
              ...(tool.customProperties ?? {}),
            },
          });
        }
        setSelectedTool(tool);
        setCurrentSubItem(tool);
      }
    },
    [
      disabled,
      selectedTool,
      openedToolMenu,
      item,
      setSelectedTool,
      setOpenedToolMenu,
      handleBoundaryModeClick,
      drawTool,
    ]
  );

  const isOpen = useMemo(() => openedToolMenu === item, [openedToolMenu, item]);

  const memoizedMenuItems = useMemo(() => {
    return ToolMapper?.[item]?.menu?.map((subItem) => (
      <DivButton
        key={subItem.label}
        className={twMerge(
          'flex w-full items-center justify-center py-2 pl-2 hover:bg-gray-80',
          disabled && 'opacity-50'
        )}
        onClick={() => {
          if (disabled) return;
          setMenuIcon((prev) => ({
            ...prev,
            [item]: subItem,
          }));
          onItemClick(subItem);
          setOpenedToolMenu(null);
        }}
      >
        {subItem?.icon !== undefined && (
          <div className="flex w-[20px] items-center pr-2 text-green-40">
            {menuIcon === subItem && <FontAwesomeIcon icon={faCheck} />}
          </div>
        )}

        <div className="flex flex-grow items-center justify-center">
          {subItem?.icon !== undefined &&
            (typeof subItem?.icon === 'object' ? (
              subItem?.icon
            ) : (
              <FontAwesomeIcon icon={subItem?.icon as IconProp} />
            ))}

          <Typography className="flex pl-2" variant="body2">
            {subItem.label}
          </Typography>
        </div>

        <div className="flex items-center justify-end pr-2">
          <Typography
            className="flex justify-end pl-2 text-gray-30"
            variant="caption"
          >
            {subItem.hotkeys}
          </Typography>
        </div>
      </DivButton>
    ));
  }, [disabled, item, menuIcon, onItemClick, setMenuIcon, setOpenedToolMenu]);

  // If the tool is not open and the parent is not the item, return null
  if (
    !isToolsOpen &&
    (selectedTool as ToolMenuContentItem)?.parent !== item &&
    selectedTool !== ToolMapper[item] &&
    !ToolMapper[item].isAlwaysVisible
  )
    return null;

  return (
    <div
      key={item}
      className={twMerge(
        'flex w-[32px] items-center justify-center',
        (ToolMapper[item].menu || customSubMenu || toolToastPopup) &&
          'relative w-[50px]',
        disabled && 'opacity-50'
      )}
    >
      <div
        key={item}
        className={twMerge(
          'flex h-[30px] w-[50px] items-center justify-center'
        )}
      >
        {/* If the tool is not selected or the parent is not the item, show the parent icon */}
        {(!selectedTool ||
          ((selectedTool as ToolMenuContentItem)?.parent !== item &&
            selectedTool !== ToolMapper[item])) && (
          <DivButton
            className={twMerge(
              'mx-1 flex h-[28px] w-[28px] items-center justify-center',
              item !== (selectedTool as ToolMenuContentItem)?.parent &&
                selectedTool !== ToolMapper[item] &&
                'hover:rounded-sm hover:bg-gray-80'
            )}
            data-tooltip-content={
              disabled
                ? undefined
                : currentSubItem?.tooltip || ToolMapper[item].tooltip
            }
            data-tooltip-delay-show={1000}
            data-tooltip-place="bottom"
            onClick={handleOnClick}
          >
            {menuIcon?.icon !== undefined ? (
              typeof menuIcon?.icon === 'object' ? (
                menuIcon?.icon
              ) : (
                <FontAwesomeIcon icon={menuIcon?.icon as IconProp} />
              )
            ) : (
              ToolMapper[item]?.icon !== undefined &&
              (typeof ToolMapper[item]?.icon === 'object' ? (
                ToolMapper[item]?.icon
              ) : (
                <FontAwesomeIcon icon={ToolMapper[item]?.icon as IconProp} />
              ))
            )}
          </DivButton>
        )}

        {/* If the tool is selected and the parent is the item, show the selected tool icon */}
        {selectedTool &&
          ((selectedTool as ToolMenuContentItem)?.parent === item ||
            selectedTool === ToolMapper[item]) && (
            <DivButton
              className={twMerge(
                'mx-1 flex h-[28px] w-[28px] items-center justify-center rounded-sm bg-green-50',
                item !== (selectedTool as ToolMenuContentItem)?.parent &&
                  selectedTool !== ToolMapper[item] &&
                  'hover:rounded-sm hover:bg-gray-80'
              )}
              data-tooltip-content={selectedTool?.tooltip}
              data-tooltip-delay-show={1000}
              data-tooltip-place="bottom"
              onClick={handleMenuClick}
            >
              {menuIcon?.icon !== undefined &&
                selectedTool?.icon === undefined &&
                (typeof menuIcon?.icon === 'object' ? (
                  menuIcon?.icon
                ) : (
                  <FontAwesomeIcon icon={menuIcon?.icon as IconProp} />
                ))}

              {selectedTool?.icon !== undefined &&
                (typeof selectedTool?.icon === 'object' ? (
                  selectedTool?.icon
                ) : (
                  <FontAwesomeIcon icon={selectedTool?.icon as IconProp} />
                ))}
            </DivButton>
          )}

        {ToolMapper[item].menu && (
          <DivButton
            className={twMerge(
              'flex h-full items-center justify-center hover:bg-gray-70',
              isOpen && 'bg-gray-80'
            )}
            data-tooltip-content={ToolMapper[item].tooltip}
            data-tooltip-delay-show={1000}
            data-tooltip-place="bottom"
            onClick={handleMenuClick}
          >
            <FontAwesomeIcon
              className={twMerge(
                'h-[8px] w-[8px] px-[4px]',
                isOpen ? 'rotate-180' : 'rotate-0'
              )}
              icon={faChevronDown}
            />
          </DivButton>
        )}
      </div>

      {ToolMapper?.[item]?.menu && !customSubMenu && isOpen && (
        <div
          className={twMerge(
            'absolute left-0 top-0 z-10 mt-10 w-[176px] bg-gray-90',
            shouldDisplayGrid
              ? 'grid w-[368px] grid-flow-col grid-cols-3 grid-rows-6 gap-x-1 p-1'
              : 'flex flex-col'
          )}
        >
          {memoizedMenuItems}
        </div>
      )}
      {customSubMenu && selectedTool === currentSubItem && (
        <div className="left-50 absolute top-0 z-10 mt-10 ">
          {customSubMenu}
        </div>
      )}
      {toolToastPopup && selectedTool === currentSubItem && (
        <div className="left-50 z-9 absolute top-0 mt-10 ">
          {toolToastPopup}
        </div>
      )}
    </div>
  );
};
